import { useRef } from "react";
import { Link } from "react-router-dom";
import type { Artist } from "@/types";
import { cn, toTitleCase } from "@/lib/utils";
import { buildCloudinaryUrl } from "@/lib/cloudinaryUrl";

interface ArtistCardProps {
  artist: Artist;
  className?: string;
}

// Darker shades for WCAG AA contrast with white text (fix for M-A11Y-1).
// green-700 (#15803d) 5.1:1, amber-700 (#b45309) 5.5:1, red-700 (#b91c1c) 5.9:1.
const statusColors = {
  open: "bg-green-700",
  closed: "bg-red-700",
  waitlist: "bg-amber-700",
} as const;

const MAX_TILT_DEG = 5;

const statusLabels = {
  open: "Open",
  closed: "Closed",
  waitlist: "Waitlist",
} as const;

export function ArtistCard({ artist, className }: ArtistCardProps) {
  const coverImageRaw = artist.coverImage || artist.portfolio[0]?.thumbnail || artist.portfolio[0]?.url;
  // Covers are stored uncropped now, so crop to the card's 16:9 around the
  // detected subject rather than blindly from the center.
  const coverImage = buildCloudinaryUrl(coverImageRaw, { width: 800, fillAspect: 16 / 9 });
  const profileImage = buildCloudinaryUrl(artist.profilePicture, { width: 80 });
  const cardRef = useRef<HTMLAnchorElement>(null);

  // Pointer-tracked tilt + light. Written straight to CSS custom properties so
  // tracking never re-renders React. Mouse/pen only — touch scrolls untouched.
  const handlePointerMove = (e: React.PointerEvent<HTMLAnchorElement>) => {
    const el = cardRef.current;
    if (!el || e.pointerType === "touch") return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    el.dataset.tracking = "true";
    el.style.setProperty("--ry", `${(px - 0.5) * 2 * MAX_TILT_DEG}deg`);
    el.style.setProperty("--rx", `${(0.5 - py) * 2 * MAX_TILT_DEG}deg`);
    el.style.setProperty("--mx", `${px * 100}%`);
    el.style.setProperty("--my", `${py * 100}%`);
  };

  const handlePointerLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    delete el.dataset.tracking;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
  };

  return (
    <Link
      ref={cardRef}
      to={`/artist/${artist.slug}`}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      className={cn(
        "tilt-card focus-ring group relative block overflow-hidden rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border)] hover:border-[var(--color-primary)]/70 hover:shadow-[0_24px_60px_-20px_rgb(0_0_0/0.8)] hover:[--lift:-6px]",
        className
      )}
    >
      {/* Cover Image - 16:9 aspect ratio */}
      <div className="relative aspect-video overflow-hidden">
        {coverImage ? (
          <img
            src={coverImage}
            alt={`${artist.name}'s cover`}
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-[var(--ease-out-expo)] group-hover:scale-[1.06]"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-primary)]/5 flex items-center justify-center">
            <span className="text-4xl text-[var(--color-text-muted)]">🎨</span>
          </div>
        )}

        {/* Status Badge - Top Right */}
        <div className="absolute top-3 right-3 z-10">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white shadow-lg",
              statusColors[artist.status]
            )}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white/80 animate-pulse" />
            {statusLabels[artist.status]}
          </span>
        </div>

        {/* Dark Gradient Overlay - Bottom anchored with rounded corners */}
        <div
          className={cn(
            "absolute bottom-0 left-0 right-0 rounded-b-xl overflow-hidden",
            "transition-[height] duration-500 ease-[var(--ease-out-expo)]",
            "h-[45%] group-hover:h-[60%]",
            "max-sm:h-[60%]"
          )}
        >
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/20 to-transparent" />

          {/* Content container */}
          <div className="relative h-full flex flex-col justify-end p-4">
            {/* Always Visible: Icon, Name, Tags */}
            <div className="flex items-center gap-3">
              {/* Profile Picture */}
              {profileImage ? (
                <img
                  src={profileImage}
                  alt={artist.name}
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-white/30 group-hover:ring-white/50 transition-all flex-shrink-0"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center ring-2 ring-white/30 flex-shrink-0">
                  <span className="text-sm font-bold text-white">
                    {artist.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}

              {/* Name & Tags */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white truncate">
                  {artist.name}
                </h3>
                {artist.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {artist.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-white/20 text-white/90 px-1.5 py-0.5 rounded"
                      >
                        {toTitleCase(tag)}
                      </span>
                    ))}
                    {artist.tags.length > 3 && (
                      <span className="text-xs bg-white/10 text-white/70 px-1.5 py-0.5 rounded">
                        +{artist.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Hover-Only: Specialty & Price - Slides up */}
            <div
              className={cn(
                "overflow-hidden transition-all duration-500 ease-[var(--ease-out-expo)]",
                "max-h-0 opacity-0",
                "group-hover:max-h-20 group-hover:opacity-100 group-hover:mt-2",
                "max-sm:max-h-20 max-sm:opacity-100 max-sm:mt-2"
              )}
            >
              {artist.specialty && (
                <p className="text-sm text-white/80 truncate">
                  {artist.specialty}
                </p>
              )}
              {artist.priceRange && (
                <p className="text-sm text-white/80 mt-1">
                  Starting from{" "}
                  <span className="font-medium text-white">
                    {artist.priceRange}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Specular light that follows the pointer */}
      <div aria-hidden className="tilt-card-light pointer-events-none absolute inset-0 z-20" />
    </Link>
  );
}
